// src/Profile.js
import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase"; 
import { updateProfile } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";

export default function Profile() {
  const [user, setUser] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [showOtpField, setShowOtpField] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState(null);

  const IMGBB_API_KEY = "18990ec99324d83b544a4e6cf99d4ffc";

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setEmail(currentUser.email || "");
        await loadProfile(currentUser.uid);
      } else {
        setUser(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const loadProfile = async (uid) => {
    try {
      const docRef = doc(db, "users", uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setName(data.name || "");
        setPhone(data.phone || "");
        setPhotoURL(data.photoURL || "");
      }
    } catch (err) {
      console.error("Error loading profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      if (data.success) {
        const imageUrl = data.data.url;
        setPhotoURL(imageUrl);
        await setDoc(doc(db, "users", user.uid), { photoURL: imageUrl }, { merge: true });
        alert("প্রোফাইল ছবি আপডেট হয়েছে!");
      }
    } catch (err) {
      alert("ছবি আপলোড ব্যর্থ হয়েছে!");
    } finally {
      setUploading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!name) { alert("দয়া করে আগে নাম লিখুন"); return; }
    const otp = Math.floor(100000 + Math.random() * 900000);
    setGeneratedOtp(otp);
    alert(`একটি কোড ${email} এ পাঠানো হয়েছে। (টেস্ট কোড: ${otp})`);
    setShowOtpField(true);
  };

  const handleVerifyAndSave = async () => {
    if (otpInput === generatedOtp?.toString()) {
      await handleSave(true);
      setShowOtpField(false);
    } else { alert("ভুল ওটিপি কোড!"); }
  };

  const handleSave = async (isVerified = false) => {
    setSaving(true);
    try {
      await updateProfile(user, { displayName: name });
      await setDoc(doc(db, "users", user.uid), {
        name, phone, email, photoURL, verified: isVerified, updatedAt: new Date()
      }, { merge: true });
      alert("প্রোফাইল সেভ হয়েছে!");
    } catch (err) {
      alert("সেভ করতে সমস্যা হয়েছে");
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="h-screen flex items-center justify-center text-[#D70F64] font-black text-xl">লোড হচ্ছে...</div>
  );

  const defaultImage = `https://ui-avatars.com/api/?name=${name || 'User'}&background=D70F64&color=fff`;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-10 px-4">
      <div className="max-w-md mx-auto p-6 bg-white rounded-[35px] shadow-2xl border border-gray-100">
        <h2 className="text-2xl font-black text-center mb-8 text-gray-800">আপনার প্রোফাইল</h2>
        
        {/* ইউজারের নাম দেখার জন্য এই অংশটি যোগ করা হয়েছে */}
        <div className="text-center mb-6">
           <h3 className="text-xl font-bold text-[#D70F64]">{name || "ইউজার"}</h3>
           <p className="text-gray-400 text-sm">{email}</p>
        </div>

        <div className="flex flex-col items-center mb-8">
          <div className="relative">
            <img src={photoURL || defaultImage} className="w-32 h-32 rounded-full object-cover border-4 border-pink-50 shadow-xl" alt="Profile" />
            <label className="absolute bottom-1 right-1 bg-[#D70F64] text-white p-2.5 rounded-full cursor-pointer hover:scale-110 transition shadow-lg border-2 border-white">
              {uploading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <span>📷</span>}
              <input type="file" className="hidden" onChange={handleImageUpload} disabled={uploading} />
            </label>
          </div>
        </div>

        <div className="space-y-6">
          <div className="group">
            <label className="text-xs font-black text-gray-400 uppercase ml-2 mb-1 block">আপনার নাম</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-4 bg-gray-50 rounded-2xl border-2 border-transparent focus:border-pink-500 focus:bg-white outline-none transition-all font-bold" placeholder="নাম লিখুন" />
          </div>

          <div>
            <label className="text-xs font-black text-gray-400 uppercase ml-2 mb-1 block">ফোন নাম্বার</label>
            <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full p-4 bg-gray-50 rounded-2xl border-2 border-transparent focus:border-pink-500 focus:bg-white outline-none transition-all font-bold" placeholder="017XXXXXXXX" />
          </div>

          {showOtpField ? (
            <div className="space-y-4">
              <input type="text" placeholder="ওটিপি কোড দিন" className="w-full p-4 border-2 border-pink-600 rounded-2xl text-center text-xl font-black tracking-widest bg-pink-50" onChange={(e) => setOtpInput(e.target.value)} />
              <button onClick={handleVerifyAndSave} className="w-full bg-green-500 text-white p-4 rounded-2xl font-black shadow-lg">ভেরিফাই করুন</button>
            </div>
          ) : (
            <button onClick={handleSendOtp} disabled={saving} className="w-full bg-[#D70F64] text-white p-4 rounded-2xl font-black shadow-lg shadow-pink-100 hover:brightness-110">
              {saving ? "সেভ হচ্ছে..." : "তথ্য আপডেট করুন"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}